import {
  PUBLICATION_VISIBILITY,
  REPORT_EMBED_CREATED_BY,
  REPORT_EMBED_SORT,
  REPORT_EMBED_TYPE,
  REPORT_GRAPH,
  REPORT_TABLE,
  SHOW,
  SHOW_ALL,
  SHOW_USER,
  SORT,
  SORT_CREATED_NEW_TO_OLD,
  SORT_CREATED_OLD_TO_NEW,
  SORT_CUSTOM,
  SORT_NAME_A_Z,
  SORT_NAME_Z_A,
  SORT_UPDATED_NEW_TO_OLD,
  SORT_UPDATED_OLD_TO_NEW,
  STATE_ALL,
  STATE_ALL_REPORT_EMBEDS,
  STATE_DRAFT,
  STATE_INTERNAL_PUBLISHED,
  STATE_PUBLISHED,
  STATE_UNPUBLISHED,
  STORY_ACCESS,
  STORY_VISIBILITY,
  TAB_ANALYTICS,
  TAB_PUBLICATIONS,
  TAB_STORIES,
  TAB_TEMPLATES,
  VIEW,
  VIEW_GRID,
  VIEW_LIST
} from '@opengov/stories-shared/constants';
import { IPublication, IStory, IStoryContent, IStoryMargins } from '@opengov/stories-shared/types';
import { ISessionUser } from './storiesTypes';
import { ISettings } from './storiesSettingsTypes';

import { stories } from '../locale';
import { IStoryPrivilege } from './claimTypes';

export interface IStoryListing {
  currentPage: number;
  data: IStory[];
  storiesUrl: string;
  total: number;
}

export interface IStoryListGraphqlRequest {
  limit: number;
  offset: number;
  opengovId?: number;
  entityId?: number;
  title?: string;
  state?: string;
  order?: { [key: string]: string } | { [key: string]: string }[];
  folder?: string;
  folderIds?: string[];
}

export interface IGetStoryGraphqlResponse {
  _id: string;
  createdAt: string;
  createdBy: string;
  description: string;
  entityId: number;
  folderId: string;
  publishedAt: string;
  publishedBy: string;
  state: string;
  title: string;
  authorId: string;
  updatedAt: string;
  updatedBy: string;
  visibility: string;
  storyKey?: string;
  thumbnail: string;
  publicUrl?: string;
  thumbnailRelativePath: string;
  paddingTop?: number;
  paddingBottom?: number;
  customCssCode?: string;
  isFollowable?: boolean;
  author: {
    _id: string;
    email: string;
    firstName: string;
    lastName: string;
    opengovId: number;
    title?: string;
  };
  folder: {
    id: string;
    publicationId: string;
    publication?: IPublication;
    title: string;
  };
  customColors?: ICustomColor[];
}

export type ICustomColor = {
  colorTarget: string;
  contentType: string;
  colors: string[];
};

export interface IStoryFoldersGraphqlRequest {
  opengovId?: number;
  entityId?: number;
}

export interface IMoveFolderGraphqlRequest {
  ids: string[];
  parentFolderId: string;
  entityId?: number;
}

export interface IMoveSectionGraphqlRequest {
  ids: string[];
  parentFolderId: string;
  entityId?: number;
  currentParentFolder: boolean;
  publicationId: string;
  isMovedToRootFolder: boolean;
}

export interface IDeleteStoriesGraphqlRequest {
  ids: string[];
}

export interface IMoveStoriesGraphqlRequest {
  ids: string[];
  folderId: string;
}

export interface ICopyStoriesGraphqlRequest {
  ids: string[];
  folderId: string;
}

export interface IMoveStoriesByFolderIdGraphqlRequest {
  currentFolderId: string;
  targetFolderId: string;
}

export enum EStoryLists {
  Stories = 'stories',
  Templates = 'templates',
  Publications = 'publications',
  Analytics = 'analytics',
  Reports = 'embedded-reports'
}

export interface IDeleteStoriesGraphqlResponse {
  update_stories: {
    affected_rows: number;
  };
}

export interface IDeleteStoryFolderGraphqlResponse {
  delete_stories: {
    returning: [
      {
        id: string;
        title: string;
      }
    ];
  };
}

export interface IMoveStoriesGraphqlResponse {
  update_stories: {
    affected_rows: number;
  };
}

export interface IMoveFolderGraphqlResponse {
  update_storyFolders: {
    affected_rows: number;
    returning: [
      {
        id: string;
        title: string;
      }
    ];
  };
}

export interface IStoryFolderInput {
  entityId: number;
  title: string;
  parentFolderId: string;
}

export interface ICreateStoryFolderGraphqlRequest {
  storyFolder: IStoryFolderInput;
}

export interface ICreateStoryFoldersGraphqlRequest {
  storyFolders: IStoryFolderInput[];
}

export interface ICopyStoryFolderGraphqlRequest {
  sourceFolderId: string;
  destinationFolderId: string;
  publicationId?: string;
}

export interface IRenameStoryFolderGraphqlRequest {
  entityId: number;
  id: string;
  title: string;
}

export interface IOrganizeStoryFoldersGraphQlRequest {
  entityId: number;
  orderedFoldersIds: string[];
  selectedFolderId: string;
  currentFolderName: string;
}

export interface IOrganizeStorySectionsGraphQlRequest {
  entityId: number;
  orderedSectionsIds: string[];
  currentSectionName: string;
  publicationId: string;
  userId: number;
}

export interface IOrganizeStoriesGraphQlRequest {
  entityId: number;
  orderedStoriesIds: string[];
  selectedFolderId: string;
  currentFolderName: string;
}

// Moved from the retired `storyListEffects.ts`. Query-param and create-story
// input shapes shared by story-list hooks/utils.
export interface IStoryListQueryObject {
  entity_id?: number;
  entitySubdomain?: string;
  og_id?: number;
  page: number;
  per_page: number;
  populate: string;
  sort?: string;
  state?: string;
  title?: string;
}

export interface ICreateStoryProps {
  isAdmin: boolean;
  isTemplate: boolean;
  createFromTemplate?: boolean;
  storyContents?: IStoryContent[];
  user: ISessionUser;
  title?: string;
  description?: string;
  entityId: number;
  entityName?: string;
  entitySubdomain: string;
  settings?: ISettings;
  folderId?: string;
  paddingTop?: number;
  paddingBottom?: number;
  customCssCode?: string;
  publication?: IPublication;
}

export interface ICreateStoryFromTemplateProps {
  description: string;
  entityId: number;
  entitySubdomain: string;
  folderId?: string;
  isAdmin: boolean;
  settings: ISettings;
  templateId: string;
  title: string;
  user: ISessionUser;
  paddingTop: number;
  paddingBottom: number;
  customCssCode?: string;
  publication?: IPublication;
}

export interface IDeleteStoryFolderGraphqlRequest {
  storyFolder: {
    entityId: number;
    title: string;
    parentFolderId: string;
  };
}

export type IStoryListMessages = typeof stories.storyList;

export interface IStoryAnalytics {
  authHeader: string;
  propertyId: string;
  data?: {
    dimensionHeaders: unknown[];
    kind: string;
    metricHeaders: unknown[];
    rowCount?: number;
    rows?: {
      dimensionValues: { value: string }[];
      metricValues: { value: string }[];
    }[];
  };
}

export interface IStoryAnalyticsPayload {
  propertyId: string;
  authHeader: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  metrics: unknown[];
  dimensions: unknown[];
}

export interface IStoryCookie {
  sort?: ISortOptions;
  storyVisibility?: IStoryVisibilityOptions;
  view?: IStoryViewOptions;
  show?: IStoryShowOptions;
  filter?: string;
  storyAccess?: IStoryPrivilege[] | string;
}

export interface IPublicationReportEmbedCookie {
  reportEmbedSearch?: string;
  reportEmbedCreatedBy?: IStoryShowOptions;
  reportEmbedSort?: ISortOptions;
  reportEmbedType?: IReportEmbedTypes;
}

export type IStoryCookieAttribute =
  | typeof SORT
  | typeof STORY_VISIBILITY
  | typeof VIEW
  | typeof SHOW
  | typeof PUBLICATION_VISIBILITY
  | typeof STORY_ACCESS;

export type IPublicationReportEmbedAttribute =
  | typeof REPORT_EMBED_CREATED_BY
  | typeof REPORT_EMBED_SORT
  | typeof REPORT_EMBED_TYPE;

export type ISortOptions =
  | typeof SORT_CREATED_NEW_TO_OLD
  | typeof SORT_CREATED_OLD_TO_NEW
  | typeof SORT_CUSTOM
  | typeof SORT_NAME_A_Z
  | typeof SORT_NAME_Z_A
  | typeof SORT_UPDATED_NEW_TO_OLD
  | typeof SORT_UPDATED_OLD_TO_NEW;

export type IStoryVisibilityOptions =
  | typeof STATE_PUBLISHED
  | typeof STATE_INTERNAL_PUBLISHED
  | typeof STATE_DRAFT
  | typeof STATE_ALL;

export type IPublicationVisibilityOptions =
  | typeof STATE_PUBLISHED
  | typeof STATE_UNPUBLISHED
  | typeof STATE_DRAFT
  | typeof STATE_ALL;

export type IReportEmbedTypes = typeof REPORT_TABLE | typeof REPORT_GRAPH | typeof STATE_ALL_REPORT_EMBEDS;

export type ITabOptions = typeof TAB_STORIES | typeof TAB_TEMPLATES | typeof TAB_ANALYTICS | typeof TAB_PUBLICATIONS;

export type IStoryViewOptions = typeof VIEW_GRID | typeof VIEW_LIST;

export type IStoryShowOptions = typeof SHOW_ALL | typeof SHOW_USER;

export enum ESelectAllStatus {
  All = 'All',
  None = 'None',
  Mixed = 'Mixed'
}

export interface IStoryListSelection {
  id: string;
  displayName: string;
}

// This represents the data returned from GraphQL and stored in storyList state
export interface IStoryFolder {
  id: string;
  title: string;
  publication?: {
    id: string;
    title: string;
  };
  subFolders: IStoryFolder[];
}

export type IStoryFolderNode = {
  id: string;
  title: string;
  storyFolder: IStoryFolder[];
};

// This represents the transformed version of IStoryFolder which we use in components
export interface IStoriesFolder {
  id: string;
  displayName: string;
  toggled?: boolean;
  disabled?: boolean;
  depth?: number;
  parent?: string;
  children?: IStoriesFolder[];
  tooltip?: string;
  order?: number;
  stories?: IStory[];
}

export interface IPublicationStoryFolder {
  id: string | undefined;
  title: string;
  displayName: string;
  toggled?: boolean;
  disabled?: boolean;
  depth?: number;
  parent?: string;
  publication?: {
    title: string;
    id: string;
  };
  subFolders?: IPublicationFolder[];
  children?: IPublicationFolder[];
  tooltip?: string;
  order?: number;
  stories?: IStory[];
}

export interface IPublicationFolder {
  storyFolder: IPublicationStoryFolder
}

export enum EProcessStatus {
  InProgress = 'in_progress',
  Success = 'success',
  Error = 'error'
}

export enum EBannerVariant {
  INFO = 'info',
  ERROR = 'error',
  SUCCESS = 'success'
}
export interface IFolderCopyProcess {
  sourceFolderId: string;
  destinationFolderId: string;
  status: EProcessStatus;
  message?: string;
  suppressDisplayBanner?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IStoriesCopyProcess {
  _id: string;
  destinationFolderId: string;
  status: EProcessStatus;
  message?: string;
  suppressDisplayBanner?: boolean;
  createdAt: string;
  updatedAt: string;
  storyCopyProcesses: IStoryCopyProcess[];
}

export interface IStoryCopyProcess {
  _id: string;
  storySourceId: string;
  storySource: IStory;
  storyCopyId?: string;
  storyCopy: IStory;
  copyProcessId: string;
}

export interface IExportPdfRequest {
  folderId: string;
  isPublicationSectionExport: boolean;
  pdfOptions: {
    display_title: boolean;
    display_date: boolean;
    display_page_number: boolean;
    enlarge_fonts: boolean;
    margins: string;
    show_margins: boolean;
  };
}

export interface IExportPdfProcess {
  id: string;
  folderId?: string;
  publicationId?: string;
  fileUrl: string;
  status: EProcessStatus;
  createdAt: string;
  suppressDisplayBanner: boolean;
}

export interface IPreviewPdfFolderRequest {
  folderId?: string;
  publicationId?: string;
  pdfOptions: {
    displayTitle: boolean;
    displayDate: boolean;
    displayPageNumber: boolean;
    enlargeFonts: boolean;
    displayStartingPageNumber?: boolean;
    margins: IStoryMargins;
    showMargins: boolean;
    template: boolean;
  };
  controller?: AbortController;
}

export interface ISelectMenuStoryOption {
  id: string;
  displayName: string;
  toggled: boolean;
  disabled: boolean;
  children: ISelectMenuStoryOption[];
  order?: number;
  isStory?: boolean;
}
export interface ICheckboxStoryNode {
  id: string;
  title: string;
  stories: ICheckboxStory[];
  subFolders: ICheckboxStoryNode[];
}

export interface ICheckboxStory {
  id: string;
  title: string;
  state: string;
}

export interface IGAPropertyDataStream {
  name: string;
  type: string;
  displayName: string;
  createTime: string;
  updateTime: string;
  webStreamData: {
    measurementId: string;
    defaultUri: string;
  };
}

export interface IGAPropertysDataStreamsResponse {
  dataStreams: IGAPropertyDataStream[];
}

export interface IGAPropertySummary {
  property: string;
  displayName: string;
  propertyType: string;
  parent: string;
}

export interface IGAAccountSummary {
  name: string;
  account: string;
  displayName: string;
  propertySummaries?: IGAPropertySummary[];
}

export interface IGAAccountSummariesResponse {
  accountSummaries: IGAAccountSummary[];
}

export interface IEmbedV2UpdateRequest {
  title: string;
  description: string;
  should_sync: boolean;
  id?: string;
  chart_type?: string;
  type?: string;
}

export interface IEmbedV3UpdateRequest {
  title: string;
  description: string;
  shouldSync: boolean;
}
